<script lang="ts">
import { computed } from 'vue';

export interface VideoSource {
  src: string;
  type?: string;
}

export interface VideoCaption {
  label?: string | null;
  srclang?: string | null;
  src?: string | null;
}

export interface PhxsgVideoPlayerVideoProps {
  sources?: VideoSource[];
  captions?: VideoCaption[];
  poster?: string | null;
  lazy?: boolean;
}

interface ParsedSource {
  type: string;
  src?: string;
  'data-src'?: string;
}

interface ParsedCaption {
  kind: 'captions';
  label?: string;
  srclang?: string;
  src?: string;
}
</script>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const { sources = [], captions = [], poster = null, lazy = true } = defineProps<PhxsgVideoPlayerVideoProps>();

const emit = defineEmits<{
  play: [Event];
  pause: [Event];
  timeupdate: [Event];
  controlshidden: [Event];
  controlsshown: [Event];
  enterfullscreen: [Event];
  exitfullscreen: [Event];
  'player-ready': [CustomEvent];
}>();

const parsedPoster = computed(() => {
  if (poster == null) {
    return {};
  }

  const isResponsive = poster.includes(',');
  const posterUrl = isResponsive ? poster.split(',', 2)[0].trim() : poster;
  const attrs: Record<string, string> = { poster: posterUrl };

  if (isResponsive) {
    attrs['data-poster'] = poster;
  }

  return attrs;
});

const parsedSources = computed<ParsedSource[]>(() => {
  return sources.map((source) => {
    const sourceSrc = source.src ?? '';
    const sourceType = source.type ?? 'video/mp4';

    const attrs: ParsedSource = { type: sourceType };

    if (sourceSrc !== '') {
      const isResponsive = sourceSrc.includes(',');

      if (lazy || isResponsive) {
        attrs['data-src'] = sourceSrc;
      } else {
        attrs.src = sourceSrc;
      }
    }

    return attrs;
  });
});

const parsedCaptions = computed<ParsedCaption[]>(() => {
  return captions.map((caption) => {
    const attrs: ParsedCaption = { kind: 'captions' };

    if (caption.label != null) {
      attrs.label = caption.label;
    }

    if (caption.srclang != null) {
      attrs.srclang = caption.srclang;
    }

    if (caption.src != null) {
      attrs.src = caption.src;
    }

    return attrs;
  });
});
</script>

<template>
  <video
    v-bind="{
      autoplay: false,
      muted: false,
      loop: false,
      playsinline: true,
      controls: true,
      ...parsedPoster,
      ...$attrs,
    }"
    @play="emit('play', $event)"
    @pause="emit('pause', $event)"
    @timeupdate="emit('timeupdate', $event)"
    @controlshidden="emit('controlshidden', $event)"
    @controlsshown="emit('controlsshown', $event)"
    @enterfullscreen="emit('enterfullscreen', $event)"
    @exitfullscreen="emit('exitfullscreen', $event)"
    @player-ready="emit('player-ready', $event as CustomEvent)"
  >
    <source v-for="(source, index) in parsedSources" :key="`source-${index}`" v-bind="source" />
    <track v-for="(caption, index) in parsedCaptions" :key="`caption-${index}`" v-bind="caption" />
  </video>
</template>
